import {Box, Skeleton, Typography} from '@mui/material'

// Layout-shaped placeholders for GET loads. Compose them so a page's skeleton
// mirrors what the page will look like once its data arrives. Saves and other
// mutations keep the global <Loading /> spinner.

const BUSY_PROPS = {role: 'status', 'aria-busy': 'true', 'aria-label': 'Loading content', 'data-testid': 'skeleton'}

// For a container that stays mounted while it loads: spread onto it to mark it as the busy region only while busy.
export const skeletonBusyProps = busy => (busy ? BUSY_PROPS : {})

// Wraps a page's skeleton so assistive tech announces a single busy region.
export const SkeletonContainer = ({children, sx, ...props}) => (
    <Box {...BUSY_PROPS} sx={sx} {...props}>
        {children}
    </Box>
)

// A line of text; height follows the font size it stands in for.
export const TextSkeleton = ({width = '60%', fontSize = 14, sx}) => <Skeleton variant="text" width={width} sx={{fontSize, ...sx}} />

// A label above an outlined input, matching the app's TextField/Select.
export const FieldSkeleton = ({labelWidth = 110, height = 56, label = true, sx}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, ...sx}}>
        {label && <TextSkeleton width={labelWidth} fontSize={14} />}
        <Skeleton variant="rounded" height={height} sx={{borderRadius: 4}} />
    </Box>
)

// Matches MuiButton's 42px pill.
export const ButtonSkeleton = ({width = 120, height = 42, sx}) => (
    <Skeleton variant="rounded" width={width} height={height} sx={{borderRadius: 4, ...sx}} />
)

export const ChipSkeleton = ({width = 72, height = 24, sx}) => (
    <Skeleton variant="rounded" width={width} height={height} sx={{borderRadius: 50, ...sx}} />
)

export const AvatarSkeleton = ({size = 40, sx}) => <Skeleton variant="circular" width={size} height={size} sx={sx} />

// A "Label / value" pair used on read-only detail pages.
export const DetailRowSkeleton = ({labelWidth = 100, valueWidth = 180, direction = 'column', sx}) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: direction,
            justifyContent: direction === 'row' ? 'space-between' : 'flex-start',
            gap: direction === 'row' ? 2 : 0.5,
            ...sx,
        }}>
        <TextSkeleton width={labelWidth} fontSize={13} />
        <TextSkeleton width={valueWidth} fontSize={15} />
    </Box>
)

// Cell content for TableSkeleton columns.
const CellSkeleton = ({column, rowIndex}) => {
    // Vary text widths a little per row so the table doesn't look stamped out.
    const widths = column.widths || ['72%', '55%', '64%', '48%', '80%']
    const width = widths[rowIndex % widths.length]
    // Stacked lines for cells like "date over time"; widths[i] is line i's width.
    const fontSizes = column.fontSizes || []
    const text =
        (column.lines || 1) > 1 ? (
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                {Array.from({length: column.lines}).map((_, line) => (
                    <TextSkeleton key={line} width={widths[line % widths.length]} fontSize={fontSizes[line] || 14} />
                ))}
            </Box>
        ) : (
            <TextSkeleton width={width} fontSize={fontSizes[0] || 14} />
        )
    switch (column.type) {
        case 'avatar':
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, width: '100%'}}>
                    <AvatarSkeleton size={32} />
                    {text}
                </Box>
            )
        case 'chip':
            return <ChipSkeleton width={column.chipWidth || 76} />
        case 'actions':
            return <Skeleton variant="circular" width={24} height={24} sx={{ml: 'auto', mr: 1}} />
        case 'checkbox':
            return <Skeleton variant="rounded" width={18} height={18} sx={{borderRadius: 0.5}} />
        default:
            return text
    }
}

// Mirrors an MUI DataGrid: header row, body rows and the DataGridFooter.
// columns: [{flex?, width?, type?: 'text'|'avatar'|'chip'|'actions'|'checkbox', widths?, lines?, fontSizes?, chipWidth?}]
export const TableSkeleton = ({columns, rows = 10, rowHeight = 52, headerHeight = 56, checkbox = false, footer = true, sx}) => {
    const allColumns = checkbox ? [{width: 52, type: 'checkbox'}, ...columns] : columns
    const cellSx = column => ({
        flex: column.width ? `0 0 ${column.width}px` : column.flex || 1,
        minWidth: 0,
        px: 1.25,
        display: 'flex',
        alignItems: 'center',
    })

    return (
        <SkeletonContainer sx={{backgroundColor: '#fbfbfb', ...sx}}>
            <Box sx={{display: 'flex', height: headerHeight, borderBottom: '1px solid #e0e0e0'}}>
                {allColumns.map((column, index) => (
                    <Box key={index} sx={cellSx(column)}>
                        {column.type === 'checkbox' ? (
                            <CellSkeleton column={column} rowIndex={0} />
                        ) : column.type !== 'actions' ? (
                            <TextSkeleton width={column.headerWidth || '45%'} fontSize={16} />
                        ) : null}
                    </Box>
                ))}
            </Box>
            {Array.from({length: rows}).map((_, rowIndex) => (
                <Box key={rowIndex} sx={{display: 'flex', height: rowHeight, borderBottom: '1px solid #e0e0e0'}}>
                    {allColumns.map((column, index) => (
                        <Box key={index} sx={cellSx(column)}>
                            <CellSkeleton column={column} rowIndex={rowIndex + index} />
                        </Box>
                    ))}
                </Box>
            ))}
            {footer && (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, px: 1}}>
                    <Box sx={{display: 'flex', gap: 1}}>
                        {Array.from({length: 7}).map((_, index) => (
                            <Skeleton key={index} variant="circular" width={32} height={32} />
                        ))}
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <TextSkeleton width={96} />
                        <Skeleton variant="rounded" width={48} height={28} />
                    </Box>
                </Box>
            )}
        </SkeletonContainer>
    )
}

// A bordered card like the ones used for mobile list views and detail panels.
export const CardSkeleton = ({children, sx}) => (
    <Box
        sx={{
            border: '1px solid #0d5d591f',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            ...sx,
        }}>
        {children}
    </Box>
)

// The mobile-view list card: avatar + name/subtitle, a couple of meta lines and a status chip.
export const ListCardSkeleton = ({avatar = true, lines = 2, chip = true, sx}) => (
    <CardSkeleton sx={sx}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            {avatar && <AvatarSkeleton size={40} />}
            <Box sx={{flex: 1}}>
                <TextSkeleton width="55%" fontSize={16} />
                <TextSkeleton width="35%" fontSize={12} />
            </Box>
            {chip && <ChipSkeleton />}
        </Box>
        {Array.from({length: lines}).map((_, index) => (
            <TextSkeleton key={index} width={index % 2 ? '50%' : '70%'} fontSize={13} />
        ))}
    </CardSkeleton>
)

export const ListCardsSkeleton = ({count = 6, gap = 2, ...cardProps}) => (
    <SkeletonContainer sx={{display: 'flex', flexDirection: 'column', gap}}>
        {Array.from({length: count}).map((_, index) => (
            <ListCardSkeleton key={index} {...cardProps} />
        ))}
    </SkeletonContainer>
)

// The visit card in the visit-action dialogs (Mark as Complete, Reschedule, Clock Out, …) while the visit loads.
// section adds that dialog's own fields: 'reportTime' (date + start/end time) or 'changeCaregiver'.
export const VisitDialogCardSkeleton = ({section}) => (
    <SkeletonContainer sx={{borderRadius: '16px', p: 2, backgroundColor: '#ffffff', boxShadow: '0px 0px 7px 0px #0000000d'}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <AvatarSkeleton size={40} />
                <Box>
                    <TextSkeleton width={150} fontSize={16} />
                    <TextSkeleton width={100} fontSize={12} />
                </Box>
            </Box>
            <ChipSkeleton width={78} />
        </Box>
        <Box
            sx={{
                mt: 3,
                p: 1,
                borderRadius: '8px',
                backgroundColor: '#0d5d5914',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
            }}>
            <TextSkeleton width={130} fontSize={12} />
            <TextSkeleton width={110} fontSize={12} />
        </Box>
        {section === 'reportTime' && (
            <Box sx={{mt: 3}}>
                <Typography variant="h8" color="text.primary">
                    Report Time
                </Typography>
                <Box sx={{mt: 3}}>
                    <Typography variant="inputLabel" color="text.secondary">
                        Date
                    </Typography>
                    <FieldSkeleton label={false} height={40} />
                </Box>
                <Box sx={{mt: 3, display: 'flex', gap: 2}}>
                    {['Start Time', 'End Time'].map(label => (
                        <Box key={label} sx={{flex: 1}}>
                            <Typography variant="inputLabel" color="text.secondary">
                                {label}
                            </Typography>
                            <FieldSkeleton label={false} height={40} />
                        </Box>
                    ))}
                </Box>
            </Box>
        )}
        {section === 'changeCaregiver' && (
            <Box sx={{mt: 3, display: 'flex', gap: 2}}>
                <Box sx={{flex: 1}}>
                    <Typography variant="inputLabel" color="text.secondary">
                        Assigned Caregiver
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, height: 40}}>
                        <AvatarSkeleton size={24} />
                        <TextSkeleton width={120} fontSize={16} />
                    </Box>
                </Box>
                <Box sx={{flex: 1}}>
                    <Typography variant="inputLabel" color="text.secondary">
                        Select New Caregiver
                    </Typography>
                    <FieldSkeleton label={false} height={40} />
                </Box>
            </Box>
        )}
    </SkeletonContainer>
)
